'use client';

import { useRouter } from 'next/navigation';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import { getUrlToBackendApi, getUrlToRouteHandlerApi, request } from '@/shared/modules/api';

import { useAuthStateContext } from './use-auth-status-context';

const MUTATION_KEY = ['auth', 'oauth-sign-out'];
const REQUEST_PATHNAME = '/oauth/logout';
const REQUEST_TO_ROUTE_HANLDER_PATHNAME = '/force-logout';

type ResponseData = void;
type MutationFnParams = {
  type: 'soft' | 'force';
};

interface useAuthSignOutMutationParams {
  redirectPathnameOnSuccess?: string;
  redirectPathnameOnError?: string;
}

export const useAuthSignOutMutation = ({
  redirectPathnameOnSuccess,
  redirectPathnameOnError,
}: useAuthSignOutMutationParams) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { setAuthStatusToNotAuthenticated } = useAuthStateContext();

  return useMutation<ResponseData, HTTPError, MutationFnParams>({
    mutationKey: MUTATION_KEY,
    mutationFn: async ({ type }) => {
      console.log(`[useAuthSignOutMutation] handling of "${type}" signOut type`);

      return type === 'soft'
        ? await request.post(getUrlToBackendApi(REQUEST_PATHNAME), { json: {} }).json()
        : await request.post(getUrlToRouteHandlerApi(REQUEST_TO_ROUTE_HANLDER_PATHNAME), { json: {} }).json();
    },
    onSuccess: async () => {
      if (redirectPathnameOnSuccess) {
        router.push(redirectPathnameOnSuccess);
      }

      /**
       * We can call `revalidatePath` (in `Server Action`) if we need to revalidate more cache types:
       * - `Router Cache`
       * - `Data Cache`
       * - `Full Route Cache`
       *
       * `router.refresh()` completely clears the Router Cache, and makes a new request to the server for the current
       * route but `refresh` does not affect the `Data Cache` or `Full Route Cache`.
       */
      router.refresh();

      setAuthStatusToNotAuthenticated();
      queryClient.invalidateQueries({ queryKey: ['auth'], refetchType: 'none' });
    },
    onError: (error) => {
      const { message } = error;

      console.group('[useAuthSignOutMutation.onError]');
      console.log(`All attempts to "signOut" were unsuccessful. Error: "${message}"`);
      console.groupEnd();

      if (redirectPathnameOnError) {
        router.push(redirectPathnameOnError);
      }

      router.refresh();
    },
  });
};
